import { baseRestApi, createTag, sendToastOnQueryFulfilled } from '@src/utils/rtkQuery';
import { globalToastManagerUI, NotificationVariants } from '@talos/kyoko';
import type {
  PreTradeAnalytic,
  PreTradeBin,
  PreTradeDataParams,
  PreTradeMarket,
  PreTradeNewOrderSingle,
  PreTradeNOSConsideredField,
  PreTradeOrderRequest,
  PreTradeRequest,
  PreTradeResponse,
  PreTradeResponseData,
} from './preTradeApiTypes';
import { clearPreTradeReference, setPreTradeReference } from './preTradeSlice';

export type {
  PreTradeAnalytic,
  PreTradeBin,
  PreTradeDataParams,
  PreTradeMarket,
  PreTradeNewOrderSingle,
  PreTradeNOSConsideredField,
  PreTradeOrderRequest,
  PreTradeRequest,
  PreTradeResponse,
  PreTradeResponseData,
};

const PRE_TRADE_ENDPOINT_PATH = '/pre-trade' as const;

// RTK Query endpoints
const preTradeApiInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    requestPreTrade: builder.mutation<PreTradeResponseData[], PreTradeRequest>({
      query: body => ({
        url: PRE_TRADE_ENDPOINT_PATH,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_result, _error, { SessionID }) => [
        createTag('PreTrade', SessionID),
        createTag('PreTrade', 'LIST'),
      ],
      onQueryStarted: sendToastOnQueryFulfilled<PreTradeRequest, PreTradeResponseData[]>({
        errorMessage: 'Pre-trade request failed',
        onError: ({ dispatch }) => {
          dispatch(clearPreTradeReference());
        },
        onSuccess: (responseData, { dispatch }, { SessionID }) => {
          if (!responseData.length) {
            globalToastManagerUI.addToast({
              text: 'Pre-trade request failed: no scenarios returned.',
              variant: NotificationVariants.Negative,
            });
            dispatch(clearPreTradeReference());
            return;
          }

          const lastScenario = responseData[responseData.length - 1];

          // The response is always HTTP 200 — scenario-level failures are signalled via Status: 'Error'
          // on the scenario itself, not via an HTTP error code.
          // The response includes full scenario history, so only check the last entry to avoid re-toasting past failures.
          if (lastScenario.Status === 'Error') {
            globalToastManagerUI.addToast({
              text: `Pre-trade request failed${lastScenario.Text ? `: ${lastScenario.Text}` : ''}.`,
              variant: NotificationVariants.Negative,
            });
            dispatch(clearPreTradeReference());
            return;
          }

          globalToastManagerUI.addToast({
            text: 'Pre-trade request successful.',
            variant: NotificationVariants.Positive,
          });
          dispatch(
            setPreTradeReference({
              sessionId: SessionID,
              requestId: lastScenario.RequestID,
              nos: lastScenario.NewOrderSingle,
            })
          );
        },
      }),
    }),
    // An empty response means the session is not yet persisted (the POST is async on the backend).
    // The UI polls until scenarios arrive rather than treating empty as an error.
    getPreTradeData: builder.query<PreTradeResponseData[], PreTradeDataParams>({
      query: params => ({
        url: PRE_TRADE_ENDPOINT_PATH,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { SessionID }) => (SessionID ? [createTag('PreTrade', SessionID)] : []),
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Failed to load pre-trade session',
      }),
    }),
    // Separate endpoint so caches can be invalidated independently:
    // getPreTradeData uses a session-scoped tag; getPreTradeHistory uses the list tag.
    getPreTradeHistory: builder.query<PreTradeResponseData[], PreTradeDataParams>({
      query: params => ({
        url: PRE_TRADE_ENDPOINT_PATH,
        method: 'GET',
        params,
      }),
      providesTags: [createTag('PreTrade', 'LIST')],
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Failed to load pre-trade history',
      }),
    }),
  }),
});

export const { useGetPreTradeDataQuery, useGetPreTradeHistoryQuery, useRequestPreTradeMutation } = preTradeApiInternal;

export const preTradeApi = {
  util: preTradeApiInternal.util,
  endpoints: preTradeApiInternal.endpoints,
};
